import { t, Trans } from '@lingui/macro';
import { useState } from "react";

import { Room } from "../../../models/Room";
import { User as UserModel } from "../../../models/User";
import { Fire } from "../../../services/FireClient";
import { My } from "../../../services/My";
import UserFindLocal from "../../components/UserFindLocal";

import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { useToast } from "../../../hooks/use-toast";
import { IconUserPlus } from '../../components/FigmaIcons';

export const AddPeopleModal: React.FC<{ room: Room }> = ({ room }) => {
  const [showModal, setShowModal] = useState(false);
  const { toast } = useToast();

  const addRoomMember = async (member: UserModel) => {
    // Check if user is already a member (client-side check for immediate feedback)
    if (room.userIds.includes(member.id)) {
      toast({
        title: t`Notice`,
        description: t`${member.name} is already added.`,
      });
      return;
    }

    await Fire.addRoomMember(room.id, member, My.user);
    toast({
      title: t`Success`,
      description: t`${member.name} has been added`,
      duration: 1500,
    });
  }

  // Handle batch addition if UserFindLocal supports it via onSelectedUsers
  const addRoomMembers = async (members: UserModel[]) => {
    for (const member of members) {
      await addRoomMember(member);
    }
  }

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogTrigger asChild>
        <Button variant="icon-panel" title={t`Add people`}>
          <IconUserPlus />
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-lg h-[600px] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-6 pb-4 border-b">
          <DialogTitle>#{room.name}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden p-4 bg-muted/30">
          <h3 className="text-sm font-medium text-muted-foreground mb-3 px-1">
            {room.workspaceId
              ? <Trans>Add members from workspace</Trans>
              : <Trans>Add members from other channels</Trans>
            }
          </h3>

          <div className="h-[calc(100%-2rem)]">
            <UserFindLocal
              onSelectedUser={addRoomMember}
              onSelectedUsers={addRoomMembers}
              placeholder={t`Search by name or email`}
              room={room}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddPeopleModal;